import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, ElementRef, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { FormControl, FormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { Invitation, InvitationType, Hub, Learnspace, LearnspaceUserRole } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { PaginatedScrollService, PaginatedScrollController } from '@shared/services/paginated-scroll.service';
import { from, Observable } from 'rxjs';
import { filter, mergeMap, map } from 'rxjs/operators';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { GenericConfirmationDialogComponent } from '@shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';

@Component({
  selector: 'send-invitation',
  templateUrl: './send-invitation.component.html',
  styleUrls: ['./send-invitation.component.scss']
})
export class SendInvitationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('url') urlInput: ElementRef;
  @ViewChildren('lsScrollContainer') private _lsScrollContainerList: QueryList<ElementRef>;

  @Input() userId: string;
  @Input() targetId: string;
  @Input() type: InvitationType;
  @Input() manageRole = false;
  @Input() feedback = true;
  @Input() showDetailList = false;
  public target: Hub | Learnspace;
  @Input() defaultRole: string;
  @Input() canDo: boolean = undefined;
  @Input() showInvitationLink = false;
  @Input() instructionMessage = 'Invite new users';

  @Output() done = new EventEmitter(null);

  public emailForm: FormGroup;
  public invitationRequestFeedback: string = null;
  public asyncErrorMessages = {
    isEmail: 'Email incorrect'
  };
  public lsFromHub: Learnspace[];
  public lsList: string[] = [];
  public invitationLink: string = null;
  public invitationLinkTooltip: string = null;
  public isRenewingLink = false;
  public splitPattern = new RegExp('[,|;|\n| |\r|\t| < | > ]');

  private _lsController: PaginatedScrollController<Learnspace>;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _courseService: CoursesAPIService,
    private _paginatedScrollService: PaginatedScrollService,
    private _dialog: MatDialog
  ) {  }

  ngOnInit(): void {
    this.emailForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      role: [this.defaultRole]
    });
    if (this.defaultRole === 'admin') {
      this.instructionMessage = 'Add administrators to your hub';
    }
    if (!this.manageRole) {
      this.emailForm.controls.role.disable();
    }
    this.emailForm.valueChanges
      .subscribe(val => {
        if (val.role === 'admin') {
          this.showDetailList = false;
        } else {
          this.showDetailList = true;
        }
      });

    this._loadTarget().subscribe(target => {
      this.target = target;
      this._resolveInvitationLink();
      if (this.showInvitationLink && !this.invitationLink) {
        this._regenerateLink();
      }
      if (this._isHub(target)) {
        this._lsController = this._paginatedScrollService.createController(
          (options) => this._courseService.getLearnspacesByHub((target as Hub).id, options).pipe(
            map(({ learnspaces, total }) => ({ total, items: learnspaces }))
          ),
          (spaces) => { this.lsFromHub = spaces; },
          { pageSize: 20, sort: 'title' }
        );
        this._lsController.reload().subscribe();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.invitationLink) {
      this.urlInput.nativeElement.setSelectionRange(0, 0);
    }
    this._lsScrollContainerList.changes.subscribe((list: QueryList<ElementRef>) => {
      if (list.first) {
        this._lsController.attachScroll(list.first.nativeElement);
      }
    });
  }

  ngOnDestroy(): void {
    this._lsController?.destroy();
  }

  public copy(): void {
    this.urlInput.nativeElement.select();
    document.execCommand('copy');
  }

  public openRegenerateLinkDialog(): void {
    this._dialog.open(GenericConfirmationDialogComponent, {
      width: '450px',
      panelClass: ['learnspace-theme'],
      data: {
        title: 'Regenerate the invitation link?',
        message: 'This will immediately invalidate the current invitation link.',
        proceedText: 'CONFIRM',
        abortText: 'CANCEL',
        dangerMode: true
      }
    })
    .afterClosed()
    .pipe(filter(val => val === 'ok'))
    .subscribe(() => this._regenerateLink());
  }

  private _regenerateLink(): void {
    this.isRenewingLink = true;
    const renew$ = this._isHub(this.target)
      ? this._courseService.renewHubInvitation(this.targetId)
      : this._courseService.renewLearnspaceInvitation(this.targetId, this.defaultRole);
    renew$.subscribe(invitation => {
      this.invitationLink = invitation.path;
      this.isRenewingLink = false;
    });
  }

  public openHelp() {
    if (this.target && this._isHub(this.target)) {
      window.open('https://help.spaces.wondavr.com/en/articles/3043277-create-and-edit-a-hub#h_d6b8133432');
    } else {
      window.open('http://help.spaces.wondavr.com/en/articles/4149124-join-a-team-space');
    }
  }

  public validateEmail(control: FormControl): Promise<any> {
    const value = control.value;
    const regexp = new RegExp('^[a-zA-Z0-9.!#$\'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$', 'g');
    const result: any = regexp.test(value) ? null : {
      isEmail: true
    };
    return result;
  }

  public submitEmail(): void {
    const mails = this.emailForm.controls.email.value;
    const role = this.emailForm.controls.role.value || this.defaultRole;
    const options: any = {};
    if (role) {
      options.role = role;
    }
    if (this.lsList.length !== 0) {
      options.learnspaceList = this.lsList;
    }
    const invitations: Invitation[] = mails.map((mail: any) => { // TODO : declare mail object structure
      return new Invitation(this.userId, mail.display, this.targetId, this.type, options);
    });
    from(invitations)
      .pipe(
        mergeMap(i => {
          return this._courseService.createInvitation(i);
        })
      )
      .subscribe(null, err => {
        this.invitationRequestFeedback = `Error: ${err.message}`;
      }, () => {
        if (this.feedback) {
          this.invitationRequestFeedback = `Your invitation has been sent to ${mails.map((m: any) => m.display).join(', ')}`; // idem
        } else {
          this.done.emit(mails);
        }
      });
  }

  public resetForm(): void {
    this.invitationRequestFeedback = null;
    this.emailForm.reset();
  }

  public isAdminRole(): boolean {
    return this.emailForm.controls.role.value === 'admin';
  }

  public addToList(learnspaceId: string, event: MatCheckboxChange): void {
    if (event.checked) {
      this.lsList.push(learnspaceId);
    } else {
      this.lsList = this.lsList.filter(ls => ls !== learnspaceId);
    }
  }

  private _loadTarget(): Observable<Hub | Learnspace> {
    if (this.type === InvitationType.joinHub) {
      return this._courseService.getHub(this.targetId);
    }
    return this._courseService.getLearnspaceById(this.targetId);
  }

  private _isHub(target: Hub | Learnspace): target is Hub {
    return !!(target as Hub).urlTitle;
  }

  private _resolveInvitationLink(): void {
    if (!this.target) return;
    if (this._isHub(this.target)) {
      this.invitationLinkTooltip = 'Share this unique URL on your internal website or by email to let people join your Hub';
      const hub = this.target as Hub;
      if (hub.properties?.invitationId || hub.properties?.invitationUrl) {
        this.invitationLink = hub.properties.invitationUrl ? hub.properties.invitationUrl : `${window.location.origin}/invitations/${hub.properties.invitationId}` || null;
      }
    } else {
      const props = (this.target as Learnspace).properties as any;
      if (this.defaultRole === LearnspaceUserRole.contributor) {
        this.invitationLink = props.contributorInvitationId ? `${window.location.origin}/invitations/${props.contributorInvitationId}` : null;
        this.invitationLinkTooltip = 'Share this link to invite contributors to your program';
      } else if (this.defaultRole === LearnspaceUserRole.learner) {
        this.invitationLink = props.learnerInvitationId ? `${window.location.origin}/invitations/${props.learnerInvitationId}` : null;
        this.invitationLinkTooltip = 'Share this link to invite learners to your program';
      }
    }
  }
}
